import * as React from 'react'
import { Plus, TriangleAlert, X } from 'lucide-react'
import type { TreeProblemDto } from '@lib/dto/tree-problem.dto'
import type { TreeProblemTypeDto } from '@lib/dto/tree-problem-type.dto'
import type { Season } from '@lib/enums/season.enum'
import {
  PROBLEM_SEVERITIES,
  PROBLEM_SEVERITY_DESCRIPTIONS,
  PROBLEM_SEVERITY_LABELS,
  type ProblemSeverity,
} from '@lib/enums/problem-severity.enum'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { SurveyPhotos } from '@/components/SurveyPhotos'
import {
  ProblemSolutions,
  type TreatmentSeasons,
} from '@/field-map/ProblemSolutions'
import { ProblemExplainer } from '@/components/ProblemExplainer'
import { SeasonPicker } from '@/field-map/SeasonPicker'
import { PriceOverride } from '@/field-map/PriceOverride'
import { priceSolution } from '@lib/helpers/solution-pricing'
import { cn } from '@/lib/utils'

/** What an "Other" problem is called wherever a catalogued one shows its name. */
const OTHER_LABEL = 'Other'

/**
 * A problem's display name.
 *
 * An archived type still has to render: a tree surveyed last year points at
 * one, and showing a blank where "Mistletoe" belongs would make a real record
 * look corrupted. It is only removed from the ADD list.
 */
function problemName(
  problem: TreeProblemDto,
  typesById: Map<string, TreeProblemTypeDto>,
): string {
  if (problem.typeId === null) return OTHER_LABEL
  return typesById.get(problem.typeId)?.name ?? 'Unknown problem'
}

/**
 * The 1-5 ladder for one problem.
 *
 * One row of five, because a ladder only reads as a ladder when its rungs are
 * side by side. The chosen rung's sentence sits underneath, where there is room
 * for what tells a 4 from a 5.
 */
function SeverityPicker({
  value,
  onChange,
  controlClassName,
}: {
  value: ProblemSeverity | null
  onChange: (severity: ProblemSeverity | null) => void
  controlClassName?: string | undefined
}) {
  return (
    <div className="space-y-1.5">
      <div className="flex gap-1">
        {PROBLEM_SEVERITIES.map((level) => {
          const active = value === level
          return (
            <button
              key={level}
              type="button"
              aria-pressed={active}
              aria-label={`${String(level)} — ${PROBLEM_SEVERITY_LABELS[level]}`}
              onClick={() => {
                // Tapping the current level clears it, so a rating made by
                // mistake needs no separate control to undo.
                onChange(active ? null : level)
              }}
              className={cn(
                'flex-1 rounded-md border text-sm font-medium transition-colors',
                controlClassName,
                active
                  ? 'border-warning bg-warning text-map-halo'
                  : 'border-default bg-surface hover:bg-subtle',
              )}
            >
              {level}
            </button>
          )
        })}
      </div>
      <p className="text-xs text-secondary-text">
        {value === null ? (
          <span className="text-muted-text">
            Not rated — optional, and fine to leave.
          </span>
        ) : (
          <>
            <span className="font-medium">
              {PROBLEM_SEVERITY_LABELS[value]}
            </span>
            {' — '}
            {PROBLEM_SEVERITY_DESCRIPTIONS[value]}
          </>
        )}
      </p>
    </div>
  )
}

/** What a problem's season means, under its picker. */
function seasonHint(season: Season | null, hasTreatments: boolean): string {
  if (season === null) return 'No season chosen, so it goes with the pruning.'
  return hasTreatments
    ? 'A treatment chosen below starts in this season.'
    : 'When this goes on the proposal.'
}

/**
 * One problem on the tree: what it is, how bad, and the evidence.
 *
 * The directory's description and solution are shown read-only rather than
 * copied into the row. They are edited in one place, they reach a client in a
 * proposal, and a copy taken at survey time would be the version a client
 * reads long after somebody improved the wording.
 */
function ProblemCard({
  problem,
  type,
  name,
  dbhInches,
  ratePerHour,
  seasons,
  isOnline,
  isPendingSync,
  controlClassName,
  onChange,
  onRemove,
}: {
  problem: TreeProblemDto
  type: TreeProblemTypeDto | undefined
  name: string
  /** The tree's diameter, for treatments charged by it. */
  dbhInches: string | null
  /** The crew rate, for treatments charged by time with no override. */
  ratePerHour: string | null
  seasons: TreatmentSeasons
  isOnline: boolean
  isPendingSync: boolean
  controlClassName?: string | undefined
  onChange: (next: TreeProblemDto) => void
  onRemove: () => void
}) {
  const isOther = problem.typeId === null

  /*
   * What the chosen treatments come to, by the same `priceSolution` each of
   * their lines uses, so the figure a price set by hand replaces is the one
   * the proposal would otherwise charge. Null when none of them prices.
   */
  const pricingsById = new Map(
    (type?.solutions ?? []).map((solution) => [solution.id, solution.pricings]),
  )
  let treatmentCents: number | null = null
  for (const chosen of problem.solutions) {
    const pricings = pricingsById.get(chosen.solutionId)
    if (pricings === undefined) continue
    const cents = priceSolution(
      { pricings, organizationRatePerHour: ratePerHour },
      { minutes: chosen.minutes, linearFeet: chosen.linearFeet, dbhInches },
    )
    if (cents !== null) treatmentCents = (treatmentCents ?? 0) + cents
  }

  const hasTreatments =
    problem.solutions.length > 0 ||
    (type?.solutions ?? []).some((solution) => solution.archivedAt === null)

  /**
   * Move the problem to another season. Its treatments still on the old one
   * were following it, so they move too; one set to a season of its own,
   * like a usual season from the problem list, keeps it.
   */
  function setSeason(season: Season): void {
    onChange({
      ...problem,
      season,
      solutions: problem.solutions.map((chosen) =>
        chosen.season === problem.season ? { ...chosen, season } : chosen,
      ),
    })
  }

  return (
    <div className="space-y-2.5 rounded-md border border-warning bg-warning-subtle p-2.5">
      <div className="flex items-start justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5 text-sm font-medium">
          <TriangleAlert className="h-4 w-4 shrink-0 text-warning" />
          <span className="truncate">{name}</span>
        </span>
        <button
          type="button"
          onClick={onRemove}
          aria-label={`Remove ${name}`}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-text hover:bg-subtle hover:text-secondary-text"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {type?.description == null ? null : (
        <p className="text-xs text-secondary-text">{type.description}</p>
      )}

      {type?.videoId == null ? null : (
        <ProblemExplainer videoId={type.videoId} />
      )}

      <SeverityPicker
        value={problem.severity}
        controlClassName={controlClassName}
        onChange={(severity) => {
          onChange({ ...problem, severity })
        }}
      />

      {/*
        A season of its own, because a problem is not always dealt with when
        the tree is pruned: winter pruning, mistletoe in spring
        (docs/seasonal-work.md).
      */}
      <div className="space-y-1.5">
        <Label>When is this dealt with?</Label>
        <SeasonPicker
          value={problem.season}
          current={seasons.current}
          label={`Season for ${name}`}
          controlClassName={controlClassName}
          onChange={setSeason}
        />
        <p className="text-xs text-muted-text">
          {seasonHint(problem.season, hasTreatments)}
        </p>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`problem-notes-${problem.id}`}>
          {isOther ? 'What is wrong?' : 'Anything specific to this tree?'}
        </Label>
        <Input
          id={`problem-notes-${problem.id}`}
          value={problem.notes ?? ''}
          placeholder={
            isOther
              ? 'Split leader, fungal body at base…'
              : 'Only the north side, over the footpath…'
          }
          className={controlClassName}
          onChange={(event) => {
            const value = event.target.value
            onChange({ ...problem, notes: value === '' ? null : value })
          }}
        />
        {/*
          Only "Other" is required, because only there does the note carry the
          whole meaning. Said before saving rather than after, since the queue
          would otherwise refuse the batch from a park with no way to fix it.
        */}
        {isOther && problem.notes === null ? (
          <p className="text-xs text-danger">
            Describe the problem, or this one cannot be saved.
          </p>
        ) : null}
      </div>

      {type === undefined ? null : (
        <ProblemSolutions
          problem={problem}
          available={type.solutions}
          dbhInches={dbhInches}
          ratePerHour={ratePerHour}
          seasons={seasons}
          controlClassName={controlClassName}
          onChange={onChange}
        />
      )}

      {/*
        One figure for the whole problem on this tree, because the problem is
        what a client accepts or declines. It also prices a problem with no
        treatment, such as an "Other" finding, which has nothing else to.
      */}
      <PriceOverride
        id={`problem-price-${problem.id}`}
        label="Price for this problem"
        formulaCents={treatmentCents}
        formulaBasis="from its treatments"
        unpricedNote={
          problem.solutions.length === 0
            ? 'no treatment chosen'
            : 'its treatments price nothing yet'
        }
        value={problem.priceOverride}
        controlClassName={controlClassName}
        onChange={(price) => {
          onChange({ ...problem, priceOverride: price })
        }}
      />

      {/*
        Photos of THIS problem, not of the tree. A stock photograph on the
        directory entry stands in when nobody took one, which is what makes a
        proposal readable for a problem nobody could get a clean shot of.
      */}
      <SurveyPhotos
        scope={{ treeProblemId: problem.id }}
        isOnline={isOnline}
        isPendingSync={isPendingSync}
        controlClassName={controlClassName}
        label="Photos of this problem"
        addLabel="Add photo"
      />
    </div>
  )
}

/**
 * Everything wrong with one tree.
 *
 * A list the surveyor builds from a directory, rather than the single flag,
 * note and rating this replaced. Those could describe exactly one finding, so
 * a tree with mistletoe in the crown AND a split leader at the base had to
 * either lose one or blur both into a paragraph nothing could sort or price.
 *
 * "Other" stays available and is deliberately last in the add list. It is the
 * escape hatch for what the directory does not cover yet — but if it is the
 * first thing a thumb reaches, the directory never grows.
 */
export function TreeProblems({
  problems,
  types,
  dbhInches,
  ratePerHour,
  seasons,
  isOnline,
  isPendingSync,
  controlClassName,
  onChange,
}: {
  problems: TreeProblemDto[]
  /**
   * The whole directory, archived entries included.
   *
   * Archived ones are dropped from the ADD list but still resolve a NAME: a
   * tree surveyed last year points at one, and rendering a blank where
   * "Mistletoe" belongs would make a real record look corrupted.
   */
  types: TreeProblemTypeDto[]
  /** The tree's diameter, passed to treatments charged by it. */
  dbhInches: string | null
  /** The crew rate, passed to treatments charged by time. */
  ratePerHour: string | null
  /** What a new problem's season, and a treatment's, start from. */
  seasons: TreatmentSeasons
  isOnline: boolean
  isPendingSync: boolean
  controlClassName?: string | undefined
  onChange: (next: TreeProblemDto[]) => void
}) {
  const [adding, setAdding] = React.useState(false)

  const typesById = React.useMemo(
    () => new Map(types.map((type) => [type.id, type])),
    [types],
  )

  // A catalogued problem can only be on a tree once, so the add list hides the
  // ones already there. "Other" is exempt: two unrelated free-text findings on
  // one tree is an ordinary thing to record.
  const taken = new Set(
    problems
      .map((problem) => problem.typeId)
      .filter((typeId): typeId is string => typeId !== null),
  )
  const available = types.filter(
    (type) => type.archivedAt === null && !taken.has(type.id),
  )

  function add(typeId: string | null): void {
    const now = new Date().toISOString()
    onChange([
      ...problems,
      {
        // Generated here, not by the server: a problem recorded in a park has
        // to have an identity before it has ever reached one, or replaying the
        // queue would insert it twice. Same rule as a tree's id.
        id: crypto.randomUUID(),
        typeId,
        notes: null,
        solutions: [],
        severity: null,
        // With the tree's pruning until somebody says otherwise.
        season: seasons.fallback,
        // Priced by its treatments until somebody sets a figure by hand.
        priceOverride: null,
        previousId: null,
        createdAt: now,
        updatedAt: now,
      },
    ])
    setAdding(false)
  }

  function replace(next: TreeProblemDto): void {
    onChange(
      problems.map((problem) => (problem.id === next.id ? next : problem)),
    )
  }

  function remove(id: string): void {
    onChange(problems.filter((problem) => problem.id !== id))
  }

  return (
    <div className="space-y-2">
      <Label>Problems</Label>

      {problems.map((problem) => (
        <ProblemCard
          key={problem.id}
          problem={problem}
          type={
            problem.typeId === null
              ? undefined
              : typesById.get(problem.typeId)
          }
          name={problemName(problem, typesById)}
          dbhInches={dbhInches}
          ratePerHour={ratePerHour}
          seasons={seasons}
          isOnline={isOnline}
          isPendingSync={isPendingSync}
          controlClassName={controlClassName}
          onChange={replace}
          onRemove={() => {
            remove(problem.id)
          }}
        />
      ))}

      {adding ? (
        <div className="space-y-1 rounded-md border border-default p-2">
          {/*
            One per row, full width. The species picker learned this the hard
            way: two columns on a phone in portrait truncates a name somebody
            typed themselves, and a problem picked by its first word is a
            problem picked wrong.
          */}
          {available.map((type) => (
            <button
              key={type.id}
              type="button"
              onClick={() => {
                add(type.id)
              }}
              className={cn(
                'flex w-full items-center rounded-md border border-default bg-surface px-2.5 text-left text-sm hover:bg-subtle',
                controlClassName,
              )}
            >
              <span className="min-w-0 truncate">{type.name}</span>
            </button>
          ))}
          <button
            type="button"
            onClick={() => {
              add(null)
            }}
            className={cn(
              'flex w-full items-center rounded-md border border-dashed border-default bg-surface px-2.5 text-left text-sm text-secondary-text hover:bg-subtle',
              controlClassName,
            )}
          >
            {OTHER_LABEL} — describe it yourself
          </button>
          <button
            type="button"
            onClick={() => {
              setAdding(false)
            }}
            className="w-full pt-1 text-xs text-muted-text hover:text-secondary-text"
          >
            Cancel
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => {
            setAdding(true)
          }}
          className={cn(
            'flex w-full items-center justify-center gap-1.5 rounded-md border border-dashed border-default bg-surface text-sm text-secondary-text hover:bg-subtle',
            controlClassName,
          )}
        >
          <Plus className="h-4 w-4" />
          {problems.length === 0 ? 'Add a problem' : 'Add another problem'}
        </button>
      )}
    </div>
  )
}
